/**
 * 63.13, the gunship (DECISIONS 270 (2), 271; core/gunship.ts): the dropship armed — called over a point, it flies in, circles
 * the point in a pylon turn with its side gun inward, holds it with fire and leaves. Its dials: the loiter, what it engages,
 * where its weapons leave its frame, and each weapon's round class (271 (6): "the tubes should be larger guided rockets, and
 * the roof mounted ones should be higher volume but lower yield and smaller rockets" — the deck's swarm of small guided
 * rockets, the sponsons' few large ones). People, seconds, radians. Starting values, to be dialled in play.
 */
import type { AutocannonCalibre, GunshipGun } from '../types';
import { CANNONS, type CannonRound } from './action';

/** A weapon's rounds: its round class (a cannon's, CANNONS; a rocket's) and how many it carries. */
export interface GunshipRound extends CannonRound { count: number }
/** A guided rocket's: popped out at `launchPeople` a second, its motor pushing it to `topPeople` at `accelPeople` a second², turning at `turn` radians a second, aiming `loft` of the ground still to go over its target (it arcs up and dives on top), spent after `life` seconds. */
export interface GuidedRound extends GunshipRound { launchPeople: number; topPeople: number; accelPeople: number; turn: number; loft: number; life: number }

export const GUNSHIP = {
  /** the side gun's calibre with no Lab pick (271 (2): lighter than the full 105) */
  gun: 40 as GunshipGun,
  /** DECISIONS 277 (2): the chin gun's calibre with no Lab pick (the 30 mm, as 63.13 built it) */
  chinGun: 30 as AutocannonCalibre,
  /**
   * The loiter: a counter-clockwise pylon turn `radiusPeople` round the point called (the side gun, on its left, inward) at
   * `speedPeople` a second, `heightPeople` over the ground at the point (never under `clearPeople` over the ground beneath it),
   * for about `seconds` — the whole laps nearest that and a half, so it leaves heading home.
   */
  loiter: { radiusPeople: 35, heightPeople: 30, clearPeople: 10, speedPeople: 14, seconds: 60 },
  /**
   * Known cover over the point (core/dropship.ts gunshipStation; the dropship's landing rule, 69.9 step 5): the orbit's centre is
   * moved away from a known enemy mount until its whole circle clears the mount's reach (and MOUNT_INTEL.marginPeople), at most
   * `coverShiftPeople` off the point — the point still well inside its reach (`reachPeople`); farther, or another known mount
   * there, the call is refused. A hot call circles where it was called.
   */
  coverShiftPeople: 25,
  /** what it engages: within `reachPeople` of the point called and `slantPeople` of itself, in its sight; `friendlyPeople`: none of its side within that of the target (danger close) */
  reachPeople: 50,
  slantPeople: 90,
  friendlyPeople: 6,
  /** a guided rocket's danger close (it follows its mark for seconds, and its own side may close on it meanwhile) */
  friendlyGuidedPeople: 10,
  /** its sensor ball under the chin (its frame: people; +x its left, y up from its feet, z its front) — where it sees from */
  eye: [0, 1.4, 2.9] as const,
  /** where each weapon's rounds leave its frame (the look's mounts; outside the hull's hit shape, core/shipBody.ts SHIP_BODY): the chin turret's muzzle, the side gun's (out of its left), the deck launchers' (left, right), the sponsons' rails and pods (left, right) */
  muzzles: {
    chin: [0, 1.35, 3.1] as const,
    side: [2.3, 1.3, -0.3] as const,
    swarm: [[1.0, 3.3, -0.6], [-1.0, 3.3, -0.6]] as const,
    heavy: [[1.8, 1.9, 0.5], [-1.8, 1.9, 0.5]] as const,
    pods: [[1.8, 1.55, -0.3], [-1.8, 1.55, -0.3]] as const,
  },
  /** the deck launchers' lean out over their sides from up (radians; the look's GUNSHIP_LAUNCHER.lean) */
  deckLean: 0.6,
  /** the cones its fixed and turreted weapons fire in (radians): the chin's bearing either side of its heading and its least depression; the side gun's bearing either side of its left and its depression; the pods' bearing either side of its heading and their depression */
  cones: { chin: { bearing: 2.1, depression: 0.09 }, side: { bearing: 0.9, depression: [0.17, 1.4] as const }, pods: { bearing: 0.45, depression: [0.05, 0.8] as const } },
  /** the chin's burst: rounds, then the rest before the next (seconds) */
  chinBurst: 6,
  chinRest: 1.2,
  /** the deck's salvo: rockets rippled, then the rest before the next (seconds); a salvo goes at two or more targets, or one structure */
  salvo: 12,
  salvoRest: 8,
  /**
   * DECISIONS 277: its pilot under fire (core/skyEvade.ts gunshipAnswer) — the risk with no dial for its side (pressing on: it
   * jinks on its way in, stays on station), and the hull's share of whole at or under which a cautious one (the dial under
   * SKY_EVADE.abortBelow) breaks off for home — real damage, never a lock alone
   */
  risk: 1,
  breakHull: 0.5,
  /**
   * 69.58 (DECISIONS 271 (7), the owner: *"Also dropship point defence is cool, we'll need to dial it in"*; GameConfig.shipTurrets):
   * the roof's point-defence turret against the missiles after its ship — a burst every `interval` seconds at the nearest within
   * `reachPeople`, a kill by point defence's chance for its `variant` (core/pointDefence.ts killChance: the missile a rocket, by
   * its burst, speed, crossing and range), from a drum of `bursts` (no refill on station). Where it sits: `at` (its frame, as
   * `eye`). Proposals.
   */
  turret: { variant: 'ciws' as const, reachPeople: 35, interval: 0.15, bursts: 40, at: [0, 3.6, 0.6] as const },
} as const;

/**
 * 63.13: each weapon's rounds (271 (6)): the chin's and the side gun's by calibre — their round classes the cannons'
 * (CANNONS, DECISIONS 277), their counts the gunship's (the 20 mm's more for the weight: its rounds are lighter) — and the
 * pods' unguided rockets.
 */
export const GUNSHIP_ROUNDS: { chin: Record<AutocannonCalibre, GunshipRound>; side: Record<GunshipGun, GunshipRound>; pods: GunshipRound } = {
  chin: { 20: { ...CANNONS[20], count: 400 }, 30: { ...CANNONS[30], count: 240 } },
  side: { 40: { ...CANNONS[40], count: 120 }, 57: { ...CANNONS[57], count: 72 }, 76: { ...CANNONS[76], count: 48 } },
  pods: { count: 14, interval: 1.5, speedPeople: 110, damage: 0, spread: 0.02, blast: { radiusPeople: 3, damage: 90 }, explosion: 'law' },
};

/** 63.13 (271 (6)): the guided rockets — the deck's swarm (many, small, low yield, rippled) and the sponsons' heavy ones (2 a side, the vehicle killers: the LAW's penetration, a bigger burst). */
export const GUNSHIP_GUIDED: { swarm: GuidedRound; heavy: GuidedRound } = {
  swarm: { count: 48, interval: 0.08, speedPeople: 25, damage: 0, spread: 0.05, blast: { radiusPeople: 2, damage: 55 }, explosion: 'gl40', launchPeople: 25, topPeople: 80, accelPeople: 120, turn: 2.5, loft: 0.5, life: 6 },
  heavy: { count: 4, interval: 5, speedPeople: 20, damage: 0, spread: 0.01, blast: { radiusPeople: 3.5, damage: 160 }, explosion: 'law', launchPeople: 20, topPeople: 100, accelPeople: 150, turn: 1.8, loft: 0.25, life: 8 },
};
